import { FC, useState } from 'react';
import { Icon } from 'learning-panel-components';
import { coreService } from 'network-utils';
import { translate } from 'translations';
import LearningPathItemContent from '@components/learningPath/content/learningPathItemContent';
import LearningPathListContent from '@components/skillTree/learningPathListContent/learningPathListContent';
import styles from './vulnerabilityInfo.module.scss';

const VulnerabilityInfo: FC<VulnerabilityInfoProps> = (props: VulnerabilityInfoProps) => {
    const { name, description, rankNumber, rankLevel, links, relatedContent } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<coreService.HelpfulContentType | null>(null);
    const [contentInfo, setContentInfo] = useState<any | undefined>([]);

    const totalItemsInRelatedContent = Object.values(relatedContent).reduce((acc, array) => acc + array.length, 0);

    const onSelect = (type: coreService.HelpfulContentType) => {
        setSelectedItem(type);
        setIsOpen(!isOpen);
        setContentInfo(relatedContent[type]);
    };

    const getIcon = (level: string) => {
        if (level === 'medium') {
            return 'vulnerabilityMed';
        }
        if (level === 'high') {
            return 'vulnerabilityHigh';
        }
        return 'vulnerabilityLow';
    };

    const stateCheckCallback = (entry: any) => {
        return entry.isCompleted;
    };

    return (
        <div className={styles['vulnerability-info-bg']}>
            <div className={styles['title-rank-wrapper']}>
                <div className={styles.title}>{name}</div>
                <div className={styles['rank-wrapper']}>
                    <Icon name={getIcon(rankLevel)} />
                    <div className={styles['rank-text']}>{translate('Rank')}</div>
                    <div className={styles['rank-value']}>{rankNumber}</div>
                </div>
            </div>
            <div className={styles.description}>{description}</div>
            <div className={styles.divider}></div>
            {links.length > 0 && (
                <div className={styles['links-wrapper-bg']}>
                    <img className={styles.layer} src={`${process.env.PUBLIC_URL}/images/links.webp`} alt="Default" />
                    <div className={styles['links-wrapper']}>
                        <div className={styles['regulation-text-wrapper']}>
                            <Icon name="openInNewTab" className={styles.icon} />
                            <div className={styles.title}>{translate('Regulation')}</div>
                        </div>

                        <div className={styles.links}>
                            {links.map((link, index) => (
                                <a key={index} href={link.url} target="_blank" rel="noreferrer">
                                    {link.tag}
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            <div className={styles.divider}></div>
            <div className={styles['related-content-wrapper']}>
                <Icon name="cbGold" />
                <div className={styles['related-content-text']}>
                    {translate('CodebashingHelpFulContent', {
                        minutes: String(totalItemsInRelatedContent)
                    })}
                </div>
            </div>
            <div className={isOpen ? styles['content-wrapper-small'] : styles['content-wrapper']}>
                {Object.entries(relatedContent).map(
                    ([type, items]) =>
                        items.length > 0 && (
                            <LearningPathItemContent
                                type={type as coreService.LearningPathContentType}
                                numberOfItems={items.length}
                                isSelected={selectedItem === type}
                                onSelect={onSelect}
                                key={type}
                                isOpen={isOpen}
                            />
                        )
                )}
            </div>
            {selectedItem !== null && (
                <LearningPathListContent content={contentInfo} stateCheckCallback={stateCheckCallback} />
            )}
        </div>
    );
};

interface Link {
    tag: string;
    url: string;
}

export interface RelatedContent {
    lesson: coreService.HelpfulContent[];
    course: coreService.HelpfulContent[];
    challenge: coreService.HelpfulContent[];
}

interface VulnerabilityInfoProps {
    name: string;
    description: string;
    rankNumber: string;
    rankLevel: 'medium' | 'high' | 'low';
    links: Link[];
    relatedContent: RelatedContent;
}

export default VulnerabilityInfo;
